<script setup lang="ts">
import type { Ref } from 'vue';
import type { ReadonlyDeep } from 'type-fest';
import type { TableFoldProps as AirRoadJobsTableFoldProps } from '@/components/tableFolds/AirRoadJobsTableFold.vue';
import type { HeadersData } from '@/composables/useTable';
import type { DialogName } from '@/stores/useDialogStore';

import { computed, reactive, ref, toRaw, watch } from 'vue';
import _cloneDeep from 'lodash/cloneDeep';

import KBtn from '@/components/btns/KBtn.vue';
import TableFoldBtn from '@/components/btns/TableFoldBtn.vue';
import TableLoader from '@/components/loaders/TableLoader.vue';
import SortThTableCell from '@/components/tableCells/SortThTableCell.vue';
import AirRoadJobsTableFold from '@/components/tableFolds/AirRoadJobsTableFold.vue';
import TableEmptyText from '@/components/texts/TableEmptyText.vue';
import KParentTooltip from '@/components/tooltips/KParentTooltip.vue';
import useTable from '@/composables/useTable';
import { useDialogStore } from '@/stores/useDialogStore';
import { fetchPostAirroadJobsDownloadLabelsFile } from '@/helpers/fetchers';

import tableStyles from '@/styles/table.module.scss';

export interface DataItem {
  id: number;
  connote: string;
  status: string;
  locationCode: string;
  totalWeight: string;
  totalVolume: string;
  items: string;
  scans: string;
  pod: string;
  fold: AirRoadJobsTableFoldProps;
}

export interface Props {
  loading?: boolean;
  data?: ReadonlyDeep<DataItem[]>;
}
const props = withDefaults(defineProps<Props>(), {
  loading: false,
  data: () => [],
});

const dialogStore = useDialogStore();

const printDialogData = reactive({
  arJobId: NaN,
  arJobConnote: '',
});

function openPrintDialog() {
  const dialogName: DialogName = 'print';
  dialogStore.open(dialogName, _cloneDeep(toRaw(printDialogData)));
}

function handlePrintClick(arJobId: number, arJobConnote: string): void {
  printDialogData.arJobId = arJobId;
  printDialogData.arJobConnote = arJobConnote;
  openPrintDialog();
}

const openFoldKey = ref(NaN);

const useTableValues = useTable(props);
const { actives, ascs, handleSort } = useTableValues;
const sortedData = useTableValues.sortedData as Readonly<
  Ref<ReadonlyDeep<DataItem[]>>
>;

const headersData = computed<HeadersData>(function () {
  // if (props.isStaff) { // use computed to keep the pattern from other tables
  return [
    {
      value: 'connote',
      title: 'Connote',
      colspan: 2,
      isNotSortable: true,
    },
    {
      value: 'status',
      title: 'Status',
      isNotSortable: true,
    },
    {
      value: 'locationCode',
      title: 'Location code',
      isNotSortable: true,
    },
    {
      value: 'totalWeight',
      title: 'Total weight',
      isNotSortable: true,
    },
    {
      value: 'totalVolume',
      title: 'Total volume',
      isNotSortable: true,
    },
    {
      value: 'items',
      title: 'Items',
      isNotSortable: true,
    },
    {
      value: 'scans',
      title: 'Scans',
      isNotSortable: true,
    },
    {
      value: 'pod',
      title: 'POD',
      isNotSortable: true,
    },
    {
      value: 'actions',
      title: 'Actions',
      isNotSortable: true,
    },
  ];
});

const foldColspan = computed(() => {
  return headersData.value.length + 1;
});

function handleClick(dataItemId: number): void {
  if (openFoldKey.value === dataItemId) {
    openFoldKey.value = NaN;
  } else {
    openFoldKey.value = dataItemId;
  }
}

const isOnlyOneFoldOpen = ref(false);
watch(
  () => props.data,
  (newData, oldData) => {
    if (isOnlyOneFoldOpen.value) return;
    // TODO
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (!newData?.length) return;
    if (newData.length === 1) openFoldKey.value = newData[0].id;
    isOnlyOneFoldOpen.value = true;
  },
  { immediate: true },
);

const loadings = ref<Record<string, boolean>>({});

async function handleDownloadLabelClick(
  dataItem: ReadonlyDeep<DataItem>,
): Promise<void> {
  if (loadings.value[dataItem.id]) return;
  loadings.value[dataItem.id] = true;
  await fetchPostAirroadJobsDownloadLabelsFile(dataItem.connote, [
    dataItem.id,
  ]).finally(() => {
    loadings.value[dataItem.id] = false;
  });
}
</script>

<template>
  <table
    :class="[
      tableStyles.table,
      tableStyles.folded,
      $style.detailedJobAirRoadJobsTable,
    ]"
    data-testid="detailedJobAirRoadJobsTable"
  >
    <thead>
      <tr>
        <template
          v-for="(headerItem, headerIndex) in headersData"
          :key="headerIndex"
        >
          <SortThTableCell
            :colspan="headerItem.colspan"
            :actives="actives"
            :ascs="ascs"
            :handle-sort="handleSort"
            :header-item-value="headerItem.value"
            :is-not-sortable="headerItem.isNotSortable"
            :class="$style[headerItem.value + 'Th']"
          >
            {{ headerItem.title }}
          </SortThTableCell>
        </template>
      </tr>
    </thead>
    <tr v-if="props.loading">
      <td :colspan="foldColspan" class="!h-64 p-0">
        <TableLoader />
      </td>
    </tr>
    <tr v-else-if="!sortedData?.length">
      <td :colspan="foldColspan" class="border-b-0 p-0">
        <TableEmptyText>No AirRoad jobs</TableEmptyText>
      </td>
    </tr>
    <tbody v-else>
      <template v-for="dataItem in sortedData" :key="dataItem.id">
        <tr
          :class="[
            tableStyles.foldTitle,
            { [tableStyles.foldOpen]: openFoldKey === dataItem.id },
          ]"
        >
          <td class="whitespace-nowrap">
            <TableFoldBtn
              :class="tableStyles.foldBtn"
              :is-open="openFoldKey === dataItem.id"
              @click="handleClick(dataItem.id)"
            />
          </td>
          <td :class="$style.connoteTd">
            {{ dataItem.connote }}
          </td>
          <td :class="$style.statusTd">
            {{ dataItem.status }}
          </td>
          <td :class="$style.locationCodeTd">
            {{ dataItem.locationCode }}
          </td>
          <td :class="$style.totalWeightTd">
            {{ dataItem.totalWeight }}
          </td>
          <td :class="$style.totalVolumeTd">
            {{ dataItem.totalVolume }}
          </td>
          <td :class="$style.itemsTd">
            {{ dataItem.items }}
          </td>
          <td :class="$style.scansTd">
            {{ dataItem.scans }}
          </td>
          <td :class="$style.podTd">
            {{ dataItem.pod }}
          </td>
          <td :class="$style.actionsTd">
            <menu :class="$style.actionsMenuList">
              <li :class="$style.actionsMenuListItem">
                <KBtn
                  variant="text"
                  icon="mdi-printer-outline"
                  @click="handlePrintClick(dataItem.id, dataItem.connote)"
                ></KBtn>
              </li>
              <li :class="$style.actionsMenuListItem">
                <KParentTooltip location="top">Download label</KParentTooltip>
                <KBtn
                  variant="text"
                  icon="mdi-download-outline"
                  :loading="loadings[dataItem.id]"
                  @click="handleDownloadLabelClick(dataItem)"
                ></KBtn>
              </li>
            </menu>
          </td>
        </tr>
        <tr
          :class="[
            tableStyles.foldContent,
            { [tableStyles.foldOpen]: openFoldKey === dataItem.id },
          ]"
        >
          <td :colspan="foldColspan">
            <div v-if="openFoldKey === dataItem.id">
              <!-- TODO readonly descendant props -->
              <!-- @vue-ignore -->
              <AirRoadJobsTableFold v-bind="dataItem.fold" />
            </div>
          </td>
        </tr>
      </template>
    </tbody>
  </table>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';

.detailedJobAirRoadJobsTable {
  & > thead > tr > th {
    white-space: nowrap;
  }
}
  .connoteTh {
    min-width: 180px;
    width: 100%;
  }
  .statusTh {
  }
  .locationCodeTh {
  }
  .totalWeightTh {
  }
  .totalVolumeTh {
  }
  .itemsTh {
  }
  .scansTh {
  }
  .podTh {
  }
  .actionsTh {
  }
  .connoteTd {
    width: 100%;
  }
  .statusTd {
  }
  .locationCodeTd {
  }
  .totalWeightTd {
  }
  .totalVolumeTd {
  }
  .itemsTd {
  }
  .scansTd {
  }
  .podTd {
  }
  .actionsTd {
    white-space: nowrap;
    text-align: center;
  }
    .actionsMenuList {
      list-style: none;
      padding: 0;
      margin: -12px -10px;
    }
      .actionsMenuListItem {
        display: inline;
      }
</style>
